import {
  createPluginFactory,
  getAboveNode,
  insertFragment,
  isBlock,
  isElement,
} from "@udecode/plate-common";

/**
 * Overrides Slate's default plain-text paste behavior, which splits text by
 * newline and calls splitNodes() + insertText() per line. The default approach
 * is non-performant for large blocks of text since it causes N React renders
 * for an N-line paste.
 *
 * This version builds a single-block Slate fragment with newlines preserved as
 * soft breaks (the same \n characters that createSoftBreakPlugin inserts on
 * Shift+Enter) and call Transforms.insertFragment() once, collapsing
 * everything into a single onChange and a single undo entry.
 *
 * Pasting as a single block also ensures select-all + delete is performant.
 */
export const createSingleBlockPastePlugin = createPluginFactory({
  key: "singleBlockPaste",
  withOverrides: (editor) => {
    const { insertTextData } = editor;

    editor.insertTextData = (data: DataTransfer) => {
      const text = data.getData("text/plain");

      // No text or single line — fall back to default
      if (!text) return insertTextData(data);
      const lines = text.split(/\r\n|\r|\n/);
      if (lines.length <= 1) return insertTextData(data);

      // Infer the block type at the cursor so inserted nodes match the schema
      const match = getAboveNode(editor, {
        match: (n) => isBlock(editor, n),
      });
      const blockType =
        match != null && isElement(match[0])
          ? (match[0] as { type: string }).type
          : "paragraph";

      // Join all lines into one block with \n soft-break characters, matching
      // what createSoftBreakPlugin inserts on Shift+Enter.
      const fragment = [
        {
          type: blockType,
          children: [{ text: lines.join("\n") }],
        },
      ];

      insertFragment(editor, fragment);
      return true;
    };

    return editor;
  },
});
